import { Modal, Table, Button, Group, Text, Center, Loader, Stack, Badge, ActionIcon, Tooltip, Flex, TextInput, CopyButton } from "@mantine/core";
import { IconCopy, IconCheck, IconSearch, IconPhone, IconMail, IconDownload } from "@tabler/icons-react";
import { useFetch } from "@mantine/hooks";
import { useContext, useMemo, useState } from "react";
import AuthContext from "../../contexts/AuthContext";
import moment from "moment/moment";

/**
 * Determina la etapa del funnel de un lead (misma lógica que el backend)
 */
const getFunnelStage = (lead) => {
    if (lead.status === 'CONVERTED') return { label: 'Convertido', color: 'green' };
    if (lead.status === 'DISQUALIFIED') return { label: 'Descalificado', color: 'red' };

    const attempts = lead.contactAttempts || [];
    if (attempts.length === 0) return { label: 'Nuevo', color: 'blue' };

    const sorted = [...attempts].sort((a, b) => new Date(b.date) - new Date(a.date));
    const lastOutcome = sorted[0]?.outcome;

    if (lastOutcome === 'FAILED' || lastOutcome === 'AUTO-FAILED') return { label: 'Perdido', color: 'red' };
    if (lastOutcome === 'WAITING-FOR-PAYMENT') return { label: 'Esperando pago', color: 'orange' };
    if (lastOutcome === 'IN-PROGRESS') return { label: 'En progreso', color: 'cyan' };
    if (lastOutcome === 'SUCCESS') return { label: 'Exitoso', color: 'green' };

    return { label: 'Contactado', color: 'gray' };
};

/**
 * Botón de copiar reutilizable
 */
const CopyBtn = ({ value, tooltip, icon: Icon = IconCopy, size = "sm" }) => (
    <CopyButton value={value} timeout={2000}>
        {({ copied, copy }) => (
            <Tooltip label={copied ? "Copiado!" : tooltip} withArrow>
                <ActionIcon
                    color={copied ? "teal" : "gray"}
                    variant="subtle"
                    onClick={copy}
                    size={size}
                >
                    {copied ? <IconCheck size={14} /> : <Icon size={14} />}
                </ActionIcon>
            </Tooltip>
        )}
    </CopyButton>
);

const STAGE_LABELS = {
    NEW: 'Nuevos',
    CONTACTED: 'Contactados',
    IN_PROGRESS: 'En negociación',
    WAITING_PAYMENT: 'Esperando pago',
    CONVERTED: 'Convertidos',
    LOST: 'Perdidos',
};

const STAGE_COLORS_MAP = {
    NEW: 'blue',
    CONTACTED: 'indigo',
    IN_PROGRESS: 'yellow',
    WAITING_PAYMENT: 'orange',
    CONVERTED: 'green',
    LOST: 'red',
};

/**
 * Modal que muestra el listado completo de leads con info de contacto.
 * Soporta filtro por etapa del embudo (funnelStage).
 */
export default function LeadsListModal({ opened, onClose, filters }) {
    const { authToken } = useContext(AuthContext);
    const [search, setSearch] = useState('');

    // Construir URL con los filtros del funnel
    const leadsUrl = useMemo(() => {
        const base = `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/dashboard-leads`;
        const params = new URLSearchParams();
        if (filters.year) params.set('year', filters.year);
        if (filters.month !== '' && filters.month !== undefined) params.set('month', filters.month);
        if (filters.country) params.set('country', filters.country);
        if (filters.academicUnitId) params.set('unitId', filters.academicUnitId);
        if (filters.funnelStage) params.set('funnelStage', filters.funnelStage);
        return `${base}?${params.toString()}`;
    }, [filters]);

    const config = {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
        },
    };

    const { data: leads, loading } = useFetch(opened ? leadsUrl : null, config);

    // Filtro local por búsqueda
    const filteredLeads = useMemo(() => {
        if (!leads || !Array.isArray(leads)) return [];
        if (!search.trim()) return leads;

        const s = search.toLowerCase();
        return leads.filter(lead =>
            (lead.name || '').toLowerCase().includes(s) ||
            (lead.lastname || '').toLowerCase().includes(s) ||
            (lead.email || '').toLowerCase().includes(s) ||
            (lead.phone || '').toLowerCase().includes(s) ||
            (lead.country || '').toLowerCase().includes(s)
        );
    }, [leads, search]);

    // Funciones de copiado masivo
    const copyAllPhones = () => {
        const phones = filteredLeads
            .map(l => l.phone)
            .filter(Boolean)
            .join('\n');
        navigator.clipboard.writeText(phones);
    };

    const copyAllEmails = () => {
        const emails = filteredLeads
            .map(l => l.email)
            .filter(Boolean)
            .join('\n');
        navigator.clipboard.writeText(emails);
    };

    const copyAllAsCSV = () => {
        const header = 'Nombre,Email,Teléfono,País,Estado,Fecha';
        const rows = filteredLeads.map(lead => {
            const stage = getFunnelStage(lead);
            return [
                `"${(lead.name || '')} ${(lead.lastname || '')}"`,
                lead.email || '',
                lead.phone || '',
                lead.country || '',
                stage.label,
                moment(lead.createdAt).format('DD/MM/YYYY'),
            ].join(',');
        });
        navigator.clipboard.writeText([header, ...rows].join('\n'));
    };

    const copyLeadInfo = (lead) => {
        const info = [
            `Nombre: ${lead.name || ''} ${lead.lastname || ''}`,
            `Email: ${lead.email || ''}`,
            `Teléfono: ${lead.phone || ''}`,
            `País: ${lead.country || ''}`,
            `Estado: ${getFunnelStage(lead).label}`,
            `Fecha: ${moment(lead.createdAt).format('DD/MM/YYYY')}`,
        ].join('\n');
        return info;
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            size="80vw"
            title={
                <Flex align="center" gap="sm">
                    <Text fw={700} size="lg">
                        Listado de Leads ({filteredLeads?.length || 0})
                    </Text>
                    {filters.funnelStage && (
                        <Badge
                            color={STAGE_COLORS_MAP[filters.funnelStage] || 'gray'}
                            variant="light"
                        >
                            {STAGE_LABELS[filters.funnelStage] || filters.funnelStage}
                        </Badge>
                    )}
                </Flex>
            }
        >
            {loading ? (
                <Center py="xl">
                    <Stack align="center" gap={4}>
                        <Loader size="sm" />
                        <Text size="sm" c="dimmed">Cargando leads...</Text>
                    </Stack>
                </Center>
            ) : (
                <Stack gap="sm">
                    {/* Barra de búsqueda + botones de copiado masivo */}
                    <Flex gap="sm" align="end" wrap="wrap">
                        <TextInput
                            placeholder="Buscar por nombre, email, teléfono..."
                            leftSection={<IconSearch size={16} />}
                            value={search}
                            onChange={(e) => setSearch(e.currentTarget.value)}
                            style={{ flex: 1, minWidth: 200 }}
                        />
                        <Group gap="xs">
                            <CopyButton value={filteredLeads.map(l => l.phone).filter(Boolean).join('\n')} timeout={2000}>
                                {({ copied, copy }) => (
                                    <Tooltip label={copied ? "Copiados!" : "Copiar todos los teléfonos"} withArrow>
                                        <Button
                                            variant="light"
                                            color={copied ? "teal" : "blue"}
                                            size="xs"
                                            leftSection={copied ? <IconCheck size={14} /> : <IconPhone size={14} />}
                                            onClick={copy}
                                        >
                                            Teléfonos
                                        </Button>
                                    </Tooltip>
                                )}
                            </CopyButton>

                            <CopyButton value={filteredLeads.map(l => l.email).filter(Boolean).join('\n')} timeout={2000}>
                                {({ copied, copy }) => (
                                    <Tooltip label={copied ? "Copiados!" : "Copiar todos los emails"} withArrow>
                                        <Button
                                            variant="light"
                                            color={copied ? "teal" : "blue"}
                                            size="xs"
                                            leftSection={copied ? <IconCheck size={14} /> : <IconMail size={14} />}
                                            onClick={copy}
                                        >
                                            Emails
                                        </Button>
                                    </Tooltip>
                                )}
                            </CopyButton>

                            <Tooltip label="Copiar todo como CSV" withArrow>
                                <Button
                                    variant="light"
                                    color="gray"
                                    size="xs"
                                    leftSection={<IconDownload size={14} />}
                                    onClick={copyAllAsCSV}
                                >
                                    CSV
                                </Button>
                            </Tooltip>
                        </Group>
                    </Flex>

                    {/* Tabla de leads */}
                    <div style={{ overflowX: 'auto' }}>
                        <Table striped highlightOnHover withTableBorder withColumnBorders fontSize="sm">
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Nombre</Table.Th>
                                    <Table.Th>Email</Table.Th>
                                    <Table.Th>Teléfono</Table.Th>
                                    <Table.Th>País</Table.Th>
                                    <Table.Th>Estado</Table.Th>
                                    <Table.Th>Fecha</Table.Th>
                                    <Table.Th style={{ width: 50 }}></Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {filteredLeads.length === 0 ? (
                                    <Table.Tr>
                                        <Table.Td colSpan={7}>
                                            <Text ta="center" c="dimmed" py="md">
                                                No se encontraron leads para los filtros seleccionados.
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                ) : (
                                    filteredLeads.map((lead) => {
                                        const stage = getFunnelStage(lead);
                                        return (
                                            <Table.Tr key={lead._id}>
                                                <Table.Td>
                                                    <Text size="sm" fw={500}>
                                                        {lead.name || ''} {lead.lastname || ''}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Group gap={4} wrap="nowrap">
                                                        <Text size="sm" style={{ wordBreak: 'break-all' }}>
                                                            {lead.email || '-'}
                                                        </Text>
                                                        {lead.email && <CopyBtn value={lead.email} tooltip="Copiar email" icon={IconMail} />}
                                                    </Group>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Group gap={4} wrap="nowrap">
                                                        <Text size="sm">{lead.phone || '-'}</Text>
                                                        {lead.phone && <CopyBtn value={lead.phone} tooltip="Copiar teléfono" icon={IconPhone} />}
                                                    </Group>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm">{lead.country || '-'}</Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Badge color={stage.color} variant="light" size="sm">
                                                        {stage.label}
                                                    </Badge>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm">
                                                        {moment(lead.createdAt).format('DD/MM/YYYY')}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <CopyBtn
                                                        value={copyLeadInfo(lead)}
                                                        tooltip="Copiar toda la info"
                                                        icon={IconCopy}
                                                    />
                                                </Table.Td>
                                            </Table.Tr>
                                        );
                                    })
                                )}
                            </Table.Tbody>
                        </Table>
                    </div>

                    {filteredLeads.length > 0 && (
                        <Text size="xs" c="dimmed" ta="right">
                            Mostrando {filteredLeads.length} de {leads?.length || 0} leads
                        </Text>
                    )}
                </Stack>
            )}
        </Modal>
    );
}
